export type TableSortDirection = 'asc' | 'desc'

export type TableSortKind = 'text' | 'date' | 'datetime' | 'number' | 'currency' | 'percent' | 'bool' | 'badge' | 'permission'

export type TableSortState<Key extends string | number | symbol = string> = {
  key: Key
  direction: TableSortDirection
}

export type TableSortColumn<Key extends string | number | symbol = string> = {
  key?: Key
  field?: Key
  kind?: TableSortKind
}

export type TableSortValueGetter<Row, Key extends string | number | symbol> = (
  row: Row,
  key: Key,
  column?: TableSortColumn<Key>,
) => unknown

export const toggleTableSort = <Key extends string | number | symbol>(
  current: TableSortState<Key> | null,
  key: Key,
): TableSortState<Key> =>
  current?.key === key
    ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
    : { key, direction: 'asc' }

export const getTableSortSuffix = <Key extends string | number | symbol>(
  sortState: TableSortState<Key> | null,
  key: Key,
): string => {
  if (sortState?.key !== key) return ''
  return sortState.direction === 'asc' ? ' ▲' : ' ▼'
}

export const compareTableValues = (a: unknown, b: unknown, kind: TableSortKind = 'text'): number => {
  const aMissing = a == null || a === ''
  const bMissing = b == null || b === ''
  if (aMissing && bMissing) return 0
  if (aMissing) return 1
  if (bMissing) return -1

  if (kind === 'number' || kind === 'percent' || kind === 'currency') {
    const an = Number(a)
    const bn = Number(b)
    if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn
  }

  if (kind === 'bool') {
    const av = a === true || a === 1 || a === '1' || String(a).toLowerCase() === 'true' || String(a).toLowerCase() === 'yes'
    const bv = b === true || b === 1 || b === '1' || String(b).toLowerCase() === 'true' || String(b).toLowerCase() === 'yes'
    return Number(av) - Number(bv)
  }

  if (kind === 'date' || kind === 'datetime') {
    const at = new Date(String(a)).getTime()
    const bt = new Date(String(b)).getTime()
    if (Number.isFinite(at) && Number.isFinite(bt)) return at - bt
  }

  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
}

export const sortTableRows = <Row, Key extends string | number | symbol>(
  rows: Row[],
  sortState: TableSortState<Key> | null,
  columns: readonly TableSortColumn<Key>[],
  getValue?: TableSortValueGetter<Row, Key>,
): Row[] => {
  if (!sortState) return rows

  const column = columns.find((item) => (item.key ?? item.field) === sortState.key)
  const direction = sortState.direction === 'asc' ? 1 : -1
  const readValue =
    getValue ??
    ((row: Row, key: Key) =>
      typeof key === 'string' || typeof key === 'number' || typeof key === 'symbol'
        ? (row as Record<Key, unknown>)[key]
        : undefined)

  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const primary =
        compareTableValues(
          readValue(a.row, sortState.key, column),
          readValue(b.row, sortState.key, column),
          column?.kind,
        ) * direction
      if (primary !== 0) return primary
      return a.index - b.index
    })
    .map((item) => item.row)
}
